import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { LifeBuoy } from "lucide-react";
import { createRecoveryKit, describeAuthError } from "../../app/authFlow.js";
import type { CreatedKit } from "../../app/authFlow.js";
import { ApiClientError } from "../../api/http.js";
import { useSession } from "../../app/session.js";
import { ACCOUNT_KEYS_KEY, useAccountKeys } from "../../data/account.js";
import { formatDateTime, formatRelativeTime } from "../../lib/format.js";
import { Dialog } from "../../ui/Dialog.js";
import { Field, SecretInput } from "../../ui/forms.js";
import { Alert, Badge, Button, Section, Skeleton } from "../../ui/primitives.js";
import { useToast } from "../../ui/Toast.js";
import { EmergencyKitView } from "./EmergencyKitView.js";

/** Settings: emergency kit status, creation and replacement. */
export function RecoverySection(props: { readonly autoOpen: boolean; readonly onAutoOpened: () => void }): ReactNode {
  const keys = useAccountKeys();
  const [open, setOpen] = useState(props.autoOpen);
  const createdAt = keys.data?.recoveryCreatedAt ?? null;

  function close(): void {
    setOpen(false);
    props.onAutoOpened();
  }

  return (
    <Section
      title="Notfall-Kit"
      description="Damit setzt du ein vergessenes Master-Passwort zurück, ohne Einträge zu verlieren. Niemand sonst – auch kein Administrator – kann das."
    >
      {keys.isPending ? (
        <Skeleton className="h-[4.5rem] w-full" />
      ) : (
        <div className="flex flex-col gap-3 rounded-lg border border-line bg-ink-2 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col items-start gap-1.5">
            {createdAt === null ? (
              <>
                <Badge tone="buoy">Kein Notfall-Kit</Badge>
                <p className="text-sm leading-relaxed text-mist">Ohne Kit ist ein vergessenes Master-Passwort endgültig verloren.</p>
              </>
            ) : (
              <>
                <Badge tone="mint">Notfall-Kit vorhanden</Badge>
                <p className="text-sm leading-relaxed text-mist">
                  Erstellt <span title={formatDateTime(createdAt)}>{formatRelativeTime(createdAt, Date.now())}</span>.
                </p>
              </>
            )}
          </div>
          <Button variant={createdAt === null ? "primary" : "secondary"} icon={LifeBuoy} onClick={(): void => setOpen(true)}>
            {createdAt === null ? "Notfall-Kit erstellen" : "Neues Kit erstellen"}
          </Button>
        </div>
      )}
      {open ? <KitDialog replacing={createdAt !== null} onClose={close} /> : null}
    </Section>
  );
}

function KitDialog(props: { readonly replacing: boolean; readonly onClose: () => void }): ReactNode {
  const { state } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [kit, setKit] = useState<CreatedKit | undefined>(undefined);
  const [saved, setSaved] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy || state.status !== "unlocked") {
      return;
    }
    if (password === "") {
      setError("Bitte gib dein Master-Passwort ein.");
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      setKit(await createRecoveryKit(state.client, state.email, password));
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_KEYS_KEY });
    } catch (err: unknown) {
      setError(err instanceof ApiClientError && err.status === 403 ? err.message : describeAuthError(err));
    } finally {
      setPassword("");
      setBusy(false);
    }
  }

  function finish(): void {
    toast.notify(props.replacing ? "Neues Notfall-Kit erstellt. Das bisherige gilt nicht mehr." : "Notfall-Kit erstellt.", { tone: "success" });
    props.onClose();
  }

  if (kit !== undefined && state.status !== "locked") {
    return (
      <Dialog open onClose={(): void => undefined} dismissible={false} title="Dein Notfall-Kit" variant="center" size="md">
        <div className="flex flex-col gap-5 p-5">
          <EmergencyKitView email={state.email} recoveryKey={kit.recoveryKey} createdAt={kit.createdAt} saved={saved} onSaved={setSaved} />
          <div className="flex justify-end">
            <Button onClick={finish} disabled={!saved}>
              Fertig
            </Button>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={(): void => {
        if (!busy) {
          props.onClose();
        }
      }}
      dismissible={!busy}
      title={props.replacing ? "Neues Notfall-Kit erstellen" : "Notfall-Kit erstellen"}
      description="Bestätige mit deinem Master-Passwort. Der Schlüssel entsteht in diesem Browser."
      variant="center"
      size="sm"
    >
      <form onSubmit={(event): void => void submit(event)} className="flex flex-col gap-5 p-5" noValidate>
        {props.replacing ? <Alert tone="info">Das bisherige Notfall-Kit gilt danach nicht mehr.</Alert> : null}
        <Field id="kit-password" label="Master-Passwort">
          <SecretInput id="kit-password" autoComplete="current-password" autoFocus value={password} disabled={busy} onChange={(event): void => setPassword(event.target.value)} revealLabel="Master-Passwort" />
        </Field>
        {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={props.onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" loading={busy}>
            Kit erstellen
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
